import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { HR_CONTACT, List, Section, Term } from './LegalPage';

const TermsOfService = () => (
  <LegalPage title="Terms of Service" lastUpdated="25 August 2026">
    <Section heading="Scope">
      <p>
        The ByteCorp HR Portal is a private internal system. It is provided to ByteCorp
        employees, contractors and interns for the administration of their employment, and to
        nobody else. It is not a public service and no account is available on request.
      </p>
      <p>
        Using the Portal means you accept these terms. They sit alongside your employment
        contract and ByteCorp's internal policies; where a policy or contract is stricter,
        the stricter obligation applies.
      </p>
    </Section>

    <Section heading="Your account">
      <List>
        <Term label="It is yours alone.">
          Do not share your password, your session, or your Google account with anyone,
          including colleagues and managers.
        </Term>
        <Term label="Keep it secure.">
          Choose a password you do not use elsewhere. If your account was created for you
          with a temporary password, change it at first sign-in.
        </Term>
        <Term label="Report compromise immediately.">
          If you think someone else has used your account, tell HR at once so the session can
          be revoked.
        </Term>
        <Term label="Google sign-in.">
          Where enabled, only your company Google account may be used, and only for an
          account that already exists in the Portal.
        </Term>
      </List>
    </Section>

    <Section heading="Acceptable use">
      <p>
        The Portal holds other people's salaries, identity documents, appraisals and medical
        or personal leave reasons. Treat it accordingly.
      </p>
      <List>
        <li>
          Access only the records your role gives you, and only for a genuine work purpose.
          Curiosity is not a work purpose.
        </li>
        <li>
          Do not attempt to bypass, probe or escalate the Portal's permission checks, and do
          not use another person's credentials or session.
        </li>
        <li>
          Do not copy, export, forward or publish another person's personal or salary data
          outside the Portal except where your role requires it.
        </li>
        <li>
          Do not scrape the Portal, automate access to it, or connect third-party tools to it
          without written authorisation.
        </li>
        <li>
          Do not upload malware, or content you have no right to upload, to an employee
          record.
        </li>
      </List>
      <p>
        Misuse of this Portal is a disciplinary matter and may also be a criminal offence and
        a breach of data protection law.
      </p>
    </Section>

    <Section heading="Confidentiality">
      <p>
        Everything you see in the Portal is confidential information belonging to ByteCorp or
        to your colleagues. Your duty of confidence over it continues after you leave.
      </p>
    </Section>

    <Section heading="Monitoring">
      <p>
        Sign-ins and changes made through the Portal are logged, including who made a change,
        to whose record, and when. These logs are kept as security and compliance evidence
        and may be reviewed in the event of a suspected breach or an audit.
      </p>
    </Section>

    <Section heading="Accuracy of records">
      <p>
        ByteCorp aims to keep your record accurate, but the Portal is an administrative
        system, not a source of legal, tax or financial advice. If something in your record
        is wrong, report it to HR rather than relying on it. Where the Portal and your
        signed employment documents disagree, the signed documents govern.
      </p>
    </Section>

    <Section heading="Availability">
      <p>
        The Portal is provided as-is. It may be unavailable for maintenance, upgrades or
        reasons outside ByteCorp's control, and no particular level of availability is
        promised. Nothing here limits any right you have under your employment contract or
        under applicable law.
      </p>
    </Section>

    <Section heading="Suspension and removal of access">
      <p>
        Your access exists because of your role. It will be changed when your role changes,
        removed when you leave, and may be suspended immediately if there is reason to
        believe these terms have been breached or an account has been compromised.
      </p>
    </Section>

    <Section heading="Personal data">
      <p>
        What the Portal holds about you, and who it is shared with, is described in the{' '}
        <Link className="font-medium text-primary-700 hover:text-primary-800" to="/privacy">
          Privacy Policy
        </Link>.
      </p>
    </Section>

    <Section heading="Changes and contact">
      <p>
        These terms may be updated; the date above will change and material changes will be
        communicated. Questions go to HR at{' '}
        <a className="font-medium text-primary-700 hover:text-primary-800" href={`mailto:${HR_CONTACT}`}>
          {HR_CONTACT}
        </a>.
      </p>
    </Section>
  </LegalPage>
);

export default TermsOfService;
